import * as React from 'react';
import { ComponentProps, ButtonSecondary, ElementLoader } from 'ui';
import styles from './embedded-favourite-button.scss';
import { withOpenState, OpenStateInjectedProps } from 'ui/common/enhancers';
import { CancelablePromise, makeCancelable } from 'app/infrastructure/js/cancelable-promise';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { PrimaryActionComponentProps } from 'app/area/case-report/case-listing/types';
import { FavouriteInstance } from 'app/area/favourite/favourite-context';
import { RenderMode } from 'app/types-shared';
import { ModalSize } from 'app/components/constants';

interface EmbeddedFavouriteButtonProps extends ComponentProps, OpenStateInjectedProps {
  readOnly?: boolean;
  textMissingSearchArguments: string;
  textSearchTitle: string;
  requiredSearchFieldsFilled: boolean;
  favouriteComponent: React.ComponentType<any>;
  renderActionComponent: (props: PrimaryActionComponentProps) => JSX.Element;
  createFavourite: () => Promise<number>;
}

interface FavouriteButtonState {
  favouriteId?: number;
}

class EmbeddedFavouriteButtonComponent
  extends React.Component<EmbeddedFavouriteButtonProps, FavouriteButtonState> {
  private loadPromise: CancelablePromise<number>;

  public state: Readonly<FavouriteButtonState> = {
    favouriteId: undefined
  };

  public componentWillUnmount() {
    if (this.loadPromise) {
      this.loadPromise.cancel();
      this.loadPromise = undefined;
    }
  }

  private handleClick = (e?: any) => {
    e && e.preventDefault();

    const { close, isOpen } = this.props;

    isOpen ? close() : this.openFavouriteDialog();
  };

  private closeDialog = () => {
    const {close} = this.props;

    close(() => {
      this.setState({
          ...this.state,
          ...{
            favouriteId: undefined
          }
        });
      });
  };

  private openFavouriteDialog = async () => {
    const { open, createFavourite, requiredSearchFieldsFilled  } = this.props;

    open();

    if(!requiredSearchFieldsFilled) {
      return;
    }

    try {
      this.loadPromise = makeCancelable(createFavourite());
      const favouriteId = await this.loadPromise.promise;
      this.setState({
        favouriteId
      });
      this.loadPromise = undefined;
    }
    catch (e) {
      if (e && e.isCanceled) {
        return;
      }
    }
  };

  public render() {
    const {
      componentId,
      className,
      label,
      isOpen,
      readOnly,
      requiredSearchFieldsFilled,
      textMissingSearchArguments,
      textSearchTitle,
      favouriteComponent: FavouriteComponent,
      renderActionComponent
    } = this.props;

    const {
      buttonWrapper,
      button
    } = styles;

    const waitingForSearchFavourite = !this.state.favouriteId && this.loadPromise;
    return (
      readOnly ? null :
      <div id={componentId} className={`form-group col-lg-4 col-xl-4 ${className} ${styles.buttonContainer}`}>
        <div className={`input-wrapper ${buttonWrapper}`}>
            <ButtonSecondary id={`${componentId}-button`} onClick={this.handleClick} classNames={button}>
              {label && label.text}
            </ButtonSecondary>
            {isOpen && <ModalWrapper {...this.props}
                  headerTitle={textSearchTitle}
                  onClose={this.closeDialog}
                  size={ModalSize.large}
                >
                {!requiredSearchFieldsFilled && <div className={styles.errorMessage}>{textMissingSearchArguments}</div>}
                {requiredSearchFieldsFilled &&
                  <div>
                    { waitingForSearchFavourite ? <ElementLoader /> :
                    <FavouriteInstance hideHeader={true} hideFilterReplacements={true}>
                      <FavouriteComponent 
                        favouriteId={this.state.favouriteId} 
                        embeddedFavourite={true} 
                        caseReportExternalProps={{
                          primaryActionComponentFactory: renderActionComponent,
                          requestedRenderMode: RenderMode.embeddedReport,
                          headerVisibilityConfiguration: {},
                          windowAsScrollableAncestor: false
                        }}
                        {...this.props} />
                    </FavouriteInstance>}
                  </div>}
              </ModalWrapper>
            }
          </div>
      </div>
    );
  }
}

const c = withOpenState(true)(EmbeddedFavouriteButtonComponent);

export { c as EmbeddedFavouriteButton };
